import { AppBar, Avatar, Box, Button, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Fade, FormControlLabel, Grid, IconButton, InputAdornment, InputBase, LinearProgress, List, ListItem, ListItemAvatar, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader, MenuItem, Paper, Switch, Tab, Tabs, TextField, Theme, Tooltip, Typography, createStyles, makeStyles, useTheme } from '@material-ui/core';
import { Add, ArrowBack, Delete, Edit, More, Save, Search, Visibility, VisibilityOff } from '@material-ui/icons';
import { Alert, Pagination } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ListItemCollapse from '../../components/ListItemCollapse';
import Logger from '../../components/Logger';
import Page from '../../containers/Page/Page';
import { useLoading } from '../../hooks/loading';
import { api } from '../../services/api';
import { ILog } from '../../types/Logger';
import { Helpers } from '../../utils/Helpers';

interface IModelo {
    id: string;
    nome: string;
    tipo: string;
    modelo_pai: string | null;
    modelo_pai_label: string | null;
    apenas_terceiros: boolean;
    composto: boolean;
    subtipo_id: string;
}

interface IDetalhe {
    id?: string;
    campo: string;
}

interface ITemplate {
    id?: string;
    template: string;
    tipo: string;
    detalhes?: IDetalhe[];
    heranca_subtipo?: string | null;
    heranca_id?: string | null;
    heranca_label?: string | null;
    heranca_template_label?: string | null;
    agrupador: boolean;
}

interface IBomba {
    id: string;
    nome: string;
}

interface ISubtipo {
    id: string;
    subtipo: string;
}

interface IAtrelamento {
    subtipo: string;
    subtipo_id: string;
    templates: ITemplate[];
}

interface IBombaData {
    data: IBomba[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

interface IModeloData {
    data: IModelo[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

interface IParams {
    id: string;
}

function ModelosCreate() {

    //Hooks
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const params = useParams<IParams>();

    //Alert
    const { enqueueSnackbar: alert } = useSnackbar();

    //Loading
    const { startLoading, stopLoading, isLoading } = useLoading();

    //Modelo
    const defaultModelo = {
        id: '',
        nome: '',
        tipo: 'ACESSORIO',
        subacessorio: false,
        modelo_pai: '',
        modelo_pai_label: '',
        apenas_terceiros: false,
        composto: false,
        subtipo_id: ''
    };

    //States
    const [error, setError] = React.useState<string>('');
    const [ativa, setAtiva] = React.useState<boolean>(true);
    const [modelo, setModelo] = React.useState<IModelo>(defaultModelo);
    const [template, setTemplate] = React.useState<ITemplate>({} as ITemplate);
    const [templates, setTemplates] = React.useState<ITemplate[]>([]);
    const [tab, setTab] = React.useState<number>(0);
    const [editDialog, setEditDialog] = React.useState<boolean>(false);
    const [editValue, setEditValue] = React.useState<ITemplate>({} as ITemplate);
    const [editIndex, setEditIndex] = React.useState<number>(0);
    const [bombas, setBombas] = React.useState<IBomba[]>([]);
    const [bombaDialog, setBombaDialog] = React.useState<boolean>(false);
    const [bombaSearch, setBombaSearch] = React.useState<string>('');
    const [bombaData, setBombaData] = React.useState<IBombaData>();
    const [modeloSearch, setModeloSearch] = React.useState<string>('');
    const [modeloData, setModeloData] = React.useState<IModeloData>();
    const [logs, setLogs] = React.useState<ILog[]>([]);
    const [subtipos, setSubtipos] = React.useState<ISubtipo[]>([]);
    const [subtiposData, setSubtiposData] = React.useState<IAtrelamento[]>([]);
    const [atrelarDialog, setAtrelarDialog] = React.useState<boolean>(false);
    const [paiDialog, setPaiDialog] = React.useState<boolean>(false);
    const [agrupadorValue, setAgrupadorValue] = React.useState<ITemplate>({} as ITemplate);
    const [agrupadorDialog, setAgrupadorDialog] = React.useState<boolean>(false);

    const tiposCampos = {
        "text": "Texto",
        "numeric": "Númerico",
        "dropdown": "Dropdown",
        "date": "Data",
    };

    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchBombas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchModelos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchSubtipos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleFetch() {
        if (params.id) {
            startLoading('main');
            try {
                const response = await api.get(`modelos/${params.id}`);
                const { object } = response.data;
                setModelo(modelo => { return { ...modelo, ...object } });
                setBombas(object.bombas);
                setAtiva(object.ativa);
                setTemplates(object.templates);
                setLogs(object.logs);
                setError('');
            } catch (error: any) {
                setError(Helpers.errorFormat(error));
            } finally {
                stopLoading('main');
            }
        }
    }

    async function handleFetchBombas(page: number = 1) {
        startLoading('bombas');
        try {
            const response = await api.get(`modelos?search=${bombaSearch}&page=${page}&tipo=BOMBA&ativa=1`);
            setBombaData(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('bombas');
        }
    }

    async function handleFetchModelos(page: number = 1) {
        startLoading('modelos');
        try {
            const response = await api.get(`modelos/pais?search=${modeloSearch}&page=${page}&&ativa=1`);
            setModeloData(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('modelos');
        }
    }

    async function handleFetchSubtipos() {
        startLoading('main');
        try {
            const response = await api.get('hierarquia');
            setSubtipos(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('main');
        }
    }

    async function handleChangeSubtipo(subtipo_id: string) {
        setModelo({ ...modelo, subtipo_id: subtipo_id });
        startLoading('atrelamentos');
        try {
            const response = await api.post(`hierarquia/${subtipo_id}/abertos`);
            setSubtiposData(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('atrelamentos');
        }
    }

    async function handleOpenAtrelarDialog() {
        setAtrelarDialog(true);
        if (modelo.subtipo_id) {
            if (subtiposData.length === 0) {
                startLoading('atrelamentos');
                try {
                    const response = await api.post(`hierarquia/${modelo.subtipo_id}/abertos`);
                    setSubtiposData(response.data.object);

                } catch (error) {
                    Helpers.error(error, alert);
                } finally {
                    stopLoading('atrelamentos');
                }
            }
        } else {
            alert('É necessário selecionar um subtipo primeiro!');
        }
    }

    async function handleChangeTipo(tipo: string) {
        let newTemplates: ITemplate[] = [];
        if (tipo === 'BOMBA') {
            try {
                const response = await api.get('templatesbomba');
                newTemplates = response.data.object;
            } catch (error) {
                Helpers.error(error, alert);
            }
        }
        setTemplates(newTemplates);
        setModelo({ ...modelo, tipo: tipo });
    }

    async function handleSave() {
        startLoading('main');
        try {
            const response = await api.request({
                url: params.id ? `modelos/${params.id}` : 'modelos',
                method: params.id ? 'PUT' : 'POST',
                data: { ...modelo, ativa, templates, bombas },
            });
            alert(response.data.message, { variant: 'success' });
            if (!params.id) { history.push(`/modelos/${response.data.id}`) }
        } catch (error: any) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('main');
        }
    }

    function handleBombasKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            handleFetchBombas(1);
        }
    }

    function handleModelosKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            handleFetchModelos(1);
        }
    }

    function getBombaName() {
        if (bombas.length > 0) {
            if (bombas.length > 1) {
                return bombas.length + ' BOMBAS';
            } else {
                return bombas[0].nome;
            }
        } else {
            return '';
        }
    }

    function toggleAcessorioComposto() {
        const value = !modelo.composto;
        setModelo({ ...modelo, composto: value });
        if (value) {
            setTemplates(templates => [
                ...templates.filter(c => !c.agrupador),
                {
                    template: 'AGRUPADOR',
                    tipo: 'text',
                    agrupador: true,

                }]);
        } else {
            setTemplates(templates =>
                templates.filter(t => t.template !== 'AGRUPADOR')
                    .map(c => { return { ...c, agrupador: false }; })
            );
        }
    }

    function setAgrupador(template: ITemplate) {
        let newTemplates = [...templates.filter(t => t.template !== 'AGRUPADOR').map(c => {
            return { ...c, agrupador: false };
        })];
        let index = templates.findIndex(t => t.template === template.template);
        newTemplates[index] = { ...template, agrupador: true };
        setTemplates(newTemplates);
    }

    function editAgrupador() {
        const agrupador = templates.find(t => t.agrupador);
        if (!agrupador) return;
        setAgrupadorValue(agrupador);
        setAgrupadorDialog(true);
    }

    function saveAgrupador() {
        const existente = templates.find(t => t.template === agrupadorValue.template && !t.agrupador);
        if (!existente && agrupadorValue.template) {
            let index = templates.findIndex(t => t.agrupador);
            let newTemplates = templates;
            newTemplates[index] = agrupadorValue;
            setTemplates(newTemplates);
            setAgrupadorDialog(false);
            setAgrupadorValue({} as ITemplate);
        } else {
            alert('Já existe uma característica com esse nome!');
        }
    }

    function addTemplate() {
        if (!templates.includes(template) && template.template) {
            setTemplates(templates => [...templates, { ...template, tipo: 'text' }]);
            setTemplate({} as ITemplate);
        }
    }

    async function editTemplate(template: ITemplate) {
        if (modelo.subtipo_id) {
            setTab(0);
            setEditValue(template);
            setEditIndex(templates.indexOf(template));
            setEditDialog(true);
            if (subtiposData.length === 0) {
                startLoading('atrelamentos');
                try {
                    const response = await api.post(`hierarquia/${modelo.subtipo_id}/abertos`);
                    setSubtiposData(response.data.object);

                } catch (error) {
                    Helpers.error(error, alert);
                } finally {
                    stopLoading('atrelamentos');
                }
            }
        } else {
            alert('É necessário selecionar um subtipo primeiro!');
        }
    }

    function saveTemplate() {
        let newTemplates = templates;
        newTemplates[editIndex] = editValue;
        setTemplates(newTemplates);
        setEditDialog(false);
        setEditValue({} as ITemplate);
    }

    function removeTemplate(template: ITemplate) {
        setTemplates(templates => templates.filter(c => c !== template));
    }

    function selectBomba(bomba: IBomba) {
        let newBombas = [];
        if (bombas.some(e => e.id === bomba.id)) {
            newBombas = bombas.filter(b => b.id !== bomba.id);
        } else {
            newBombas = bombas.filter(b => b.id !== bomba.id);
            newBombas = [...bombas, bomba];
        }
        setBombas(newBombas);
    }

    function selectPai(selected: IModelo) {
        let newModelo;

        if (modelo.modelo_pai === selected.id) {
            newModelo = {
                ...modelo,
                modelo_pai: null,
                modelo_pai_label: null
            }
        } else {
            newModelo = {
                ...modelo,
                modelo_pai: selected.id,
                modelo_pai_label: selected.nome,
            }
        }

        setModelo(newModelo);
    }

    function selectAtrelar(subtipo: IAtrelamento, selected: ITemplate) {
        let newTemplate;

        if (template.heranca_id === selected.id) {
            newTemplate = {
                ...template,
                heranca_subtipo: null,
                heranca_id: null,
                heranca_label: null,
                heranca_template_label: null
            }
        } else {
            newTemplate = {
                ...template,
                heranca_subtipo: subtipo.subtipo_id,
                heranca_id: selected.id,
                heranca_label: subtipo.subtipo,
                heranca_template_label: selected.template
            }
        }

        setTemplate(newTemplate);
    }

    function selectEditAtrelar(subtipo: IAtrelamento, selected: ITemplate) {
        let newTemplate;

        if (editValue.heranca_id === selected.id) {
            newTemplate = {
                ...editValue,
                heranca_subtipo: null,
                heranca_id: null,
                heranca_label: null,
                heranca_template_label: null
            }
        } else {
            newTemplate = {
                ...editValue,
                heranca_subtipo: subtipo.subtipo_id,
                heranca_id: selected.id,
                heranca_label: subtipo.subtipo,
                heranca_template_label: selected.template
            }
        }

        setEditValue(newTemplate);
    }

    function isTemplateVisible(template: ITemplate) {
        if (template.template.startsWith('INFO_')) {
            return modelo.composto ? true : false;
        } else {
            return modelo.composto ? false : true;
        }
    }

    return (
        <Page error={error} loading={isLoading('main')}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {params.id ? 'Editar Modelo' : 'Novo Modelo'}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    {params.id &&
                        <Box mr={2}>
                            <Logger
                                logs={logs}
                            />
                        </Box>
                    }
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => handleSave()}>
                        Salvar
                    </Button>
                </Box>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        {params.id &&
                            <Box mb={2} display="flex" justifyContent="end">
                                <FormControlLabel
                                    control={<Switch checked={Boolean(ativa)} onChange={(event) => setAtiva(event.target.checked)} name="ativa" />}
                                    label="Ativo"
                                />
                            </Box>
                        }
                        <Grid className={classes.row} container spacing={2}>
                            <Grid item xs={12} md={!modelo.tipo || modelo.tipo === "BOMBA" ? 8 : 4}>
                                <TextField
                                    id="nome"
                                    name="nome"
                                    label="Nome do Modelo"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={modelo.nome || ''}
                                    onChange={(event) => setModelo({ ...modelo, nome: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="tipo"
                                    name="tipo"
                                    label="Tipo do Modelo"
                                    variant="outlined"
                                    select={true}
                                    fullWidth={true}
                                    value={modelo.tipo || ''}
                                    onChange={(event) => handleChangeTipo(event.target.value)}
                                >
                                    <MenuItem value="BOMBA">Bomba</MenuItem>
                                    <MenuItem value="ACESSORIO">Acessório</MenuItem>
                                </TextField>
                            </Grid>
                            {modelo.tipo === "ACESSORIO" &&
                                <React.Fragment>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            id="bomba_id"
                                            name="bomba_id"
                                            label="Bombas"
                                            variant="outlined"
                                            disabled={true}
                                            fullWidth={true}
                                            value={getBombaName()}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Selecionar">
                                                            <IconButton color="primary" edge="end" onClick={() => setBombaDialog(true)}>
                                                                <Search />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Dialog open={bombaDialog} fullWidth={true} maxWidth="xs">
                                            <AppBar style={{ position: 'relative' }}>
                                                <DialogTitle>Selecionar Bombas</DialogTitle>
                                                <Box mx={3} mb={3}>
                                                    <Paper className={classes.custom}>
                                                        <InputBase
                                                            autoFocus
                                                            disabled={isLoading('bombas')}
                                                            className={classes.input}
                                                            placeholder="Pesquisa"
                                                            onChange={(event) => setBombaSearch(event.target.value)}
                                                            onKeyUp={(event) => handleBombasKeyUp(event)}
                                                            value={bombaSearch}
                                                        />
                                                        <IconButton
                                                            disabled={isLoading('bombas')}
                                                            style={{ padding: 10 }}
                                                            onClick={() => handleFetchBombas()}
                                                        >
                                                            <Search />
                                                        </IconButton>
                                                    </Paper>
                                                    <Box mt={2}>
                                                        <Grid container justifyContent="center">
                                                            <Pagination
                                                                page={bombaData?.current_page || 1}
                                                                count={bombaData?.last_page || 1}
                                                                onChange={(_event, page) => handleFetchBombas(page)}
                                                                color="secondary"
                                                            />
                                                        </Grid>
                                                    </Box>
                                                </Box>
                                                <Fade in={isLoading('bombas')} timeout={500}>
                                                    <LinearProgress color="secondary"></LinearProgress>
                                                </Fade>
                                            </AppBar>
                                            <DialogContent>
                                                <List>
                                                    {bombaData && bombaData.data && bombaData.data.map((bomba, index) => (
                                                        <ListItem button key={index} onClick={() => selectBomba(bomba)}>
                                                            <ListItemIcon>
                                                                <Checkbox
                                                                    edge="start"
                                                                    checked={bombas.some(e => e.id === bomba.id)}
                                                                    tabIndex={-1}
                                                                    disableRipple={true}
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={bomba.nome}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button color="primary" onClick={() => setBombaDialog(false)}>
                                                    Fechar
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            id="subtipo_id"
                                            name="subtipo_id"
                                            label="Subtipo do Modelo"
                                            variant="outlined"
                                            select={true}
                                            fullWidth={true}
                                            value={modelo.subtipo_id || ''}
                                            onChange={(event) => handleChangeSubtipo(event.target.value)}
                                        >
                                            {subtipos && subtipos.map((subtipo, index) => (
                                                <MenuItem key={index} value={subtipo.id}>{subtipo.subtipo}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            id="modelo_pai"
                                            name="modelo_pai"
                                            label="Modelo Pai"
                                            variant="outlined"
                                            disabled={true}
                                            fullWidth={true}
                                            value={modelo.modelo_pai_label || ''}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Selecionar">
                                                            <IconButton color="primary" edge="end" onClick={() => setPaiDialog(true)}>
                                                                <Search />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Dialog open={paiDialog} fullWidth={true} maxWidth="xs">
                                            <AppBar style={{ position: 'relative' }}>
                                                <DialogTitle>Selecionar Modelo Pai</DialogTitle>
                                                <Box mx={3} mb={3}>
                                                    <Paper className={classes.custom}>
                                                        <InputBase
                                                            autoFocus
                                                            disabled={isLoading('modelos')}
                                                            className={classes.input}
                                                            placeholder="Pesquisa"
                                                            onChange={(event) => setModeloSearch(event.target.value)}
                                                            onKeyUp={(event) => handleModelosKeyUp(event)}
                                                            value={modeloSearch}
                                                        />
                                                        <IconButton
                                                            disabled={isLoading('modelos')}
                                                            style={{ padding: 10 }}
                                                            onClick={() => handleFetchModelos()}
                                                        >
                                                            <Search />
                                                        </IconButton>
                                                    </Paper>
                                                    <Box mt={2}>
                                                        <Grid container justifyContent="center">
                                                            <Pagination
                                                                page={modeloData?.current_page || 1}
                                                                count={modeloData?.last_page || 1}
                                                                onChange={(_event, page) => handleFetchModelos(page)}
                                                                color="secondary"
                                                            />
                                                        </Grid>
                                                    </Box>
                                                </Box>
                                                <Fade in={isLoading('modelos')} timeout={500}>
                                                    <LinearProgress color="secondary"></LinearProgress>
                                                </Fade>
                                            </AppBar>
                                            <DialogContent>
                                                {!isLoading('modelos') &&
                                                    <List>
                                                        {modeloData && modeloData.data && modeloData.data.length > 0 ? modeloData.data.map((m, index) => (
                                                            <ListItem button key={index} onClick={() => selectPai(m)}>
                                                                <ListItemIcon>
                                                                    <Checkbox
                                                                        edge="end"
                                                                        checked={m.id === modelo.modelo_pai}
                                                                        tabIndex={-1}
                                                                        disableRipple={true}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={m.nome}
                                                                />
                                                            </ListItem>
                                                        )) :
                                                            <ListItem>
                                                                <ListItemText primary="Nenhuma possibilidade de escolha encontrada." />
                                                            </ListItem>
                                                        }
                                                    </List>
                                                }
                                            </DialogContent>
                                            <DialogActions>
                                                <Button color="primary" onClick={() => setPaiDialog(false)}>
                                                    Fechar
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </Grid>
                                </React.Fragment>
                            }
                        </Grid>
                        <Grid className={classes.row} container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(modelo.apenas_terceiros)}
                                            name="apenas_terceiros"
                                            color="secondary"
                                            onChange={(event) => setModelo({ ...modelo, apenas_terceiros: event.target.checked })}
                                        />
                                    }
                                    label="Apenas Cotação"
                                />
                            </Grid>
                            {modelo.tipo === 'ACESSORIO' &&
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(modelo.composto)}
                                                name="composto"
                                                color="secondary"
                                                onChange={(event) => toggleAcessorioComposto()}
                                            />
                                        }
                                        label="Acessório Composto"
                                    />
                                </Grid>
                            }
                        </Grid>
                        <Box mb={2}>
                            <Divider></Divider>
                        </Box>
                        <Grid className={classes.row} container spacing={2}>
                            <Grid item xs={6} md={!modelo.tipo || modelo.tipo === 'BOMBA' ? 8 : 4}>
                                <TextField
                                    label="Característica"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    value={template.template || ''}
                                    onChange={(event) => setTemplate({ ...template, template: event.target.value })}
                                />
                            </Grid>
                            {modelo.tipo === 'ACESSORIO' &&
                                <React.Fragment>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Atrelar Característica"
                                            variant="outlined"
                                            disabled={true}
                                            fullWidth={true}
                                            value={template.heranca_template_label ? `${template.heranca_template_label} (${template.heranca_label})` : ''}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Selecionar">
                                                            <IconButton color="primary" edge="end" onClick={() => handleOpenAtrelarDialog()}>
                                                                <Search />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Dialog open={atrelarDialog} fullWidth={true} maxWidth="xs">
                                        <AppBar style={{ position: 'relative' }}>
                                            <DialogTitle>Atrelar Característica</DialogTitle>
                                            <Box px={3} mb={2}>
                                                <Typography variant="body2" color="initial">
                                                    Selecione uma característica para atrelar.
                                                </Typography>
                                            </Box>
                                            <Fade in={isLoading('atrelamentos')} timeout={500}>
                                                <LinearProgress color="secondary"></LinearProgress>
                                            </Fade>
                                        </AppBar>
                                        <DialogContent>
                                            {!isLoading('atrelamentos') &&
                                                <List>
                                                    {subtiposData && subtiposData.length > 0 ? subtiposData.map((subtipo, index) => (
                                                        <ListItemCollapse key={index} title={subtipo.subtipo}>
                                                            {subtipo.templates.map((t, i) => (
                                                                <ListItem button key={i} onClick={() => selectAtrelar(subtipo, t)}>
                                                                    <ListItemIcon>
                                                                        <Checkbox
                                                                            edge="end"
                                                                            checked={t.id === template.heranca_id}
                                                                            tabIndex={-1}
                                                                            disableRipple={true}
                                                                        />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={t.template}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </ListItemCollapse>
                                                    )) :
                                                        <ListItem>
                                                            <ListItemText primary="Nenhuma possibilidade de atrelamento encontrado." />
                                                        </ListItem>
                                                    }
                                                </List>
                                            }
                                        </DialogContent>
                                        <DialogActions>
                                            <Button color="primary" onClick={() => setAtrelarDialog(false)}>
                                                Fechar
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </React.Fragment>
                            }
                            <Grid item xs={6} md={4}>
                                <Button
                                    style={{ height: '100%' }}
                                    color="secondary"
                                    variant="contained"
                                    startIcon={<Add />}
                                    fullWidth={true}
                                    disabled={!params.id && modelo.tipo === 'BOMBA'}
                                    onClick={() => addTemplate()}>
                                    Adicionar
                                </Button>
                            </Grid>
                        </Grid>
                        {params.id && modelo.tipo === 'BOMBA' &&
                            <Box mb={2}>
                                <Alert severity="warning" style={{ alignItems: 'center' }}>
                                    As alterações feitas serão aplicadas a <strong>todos</strong> os modelos de bomba.
                                </Alert>
                            </Box>
                        }
                        <Card variant="outlined">
                            <CardContent>
                                <List dense>
                                    {templates.filter(c => c.agrupador).length > 0 &&
                                        <ListSubheader>AGRUPADOR</ListSubheader>
                                    }
                                    {templates.filter(c => c.agrupador).map((template, index) => (
                                        <ListItem key={index}>
                                            <ListItemAvatar>
                                                <Avatar style={{ backgroundColor: theme.palette.secondary.main }}>
                                                    <More style={{ color: theme.palette.secondary.contrastText }} />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={template.template}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton edge="start" onClick={() => editAgrupador()}>
                                                    <Edit />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                    <ListSubheader>CARACTERÍSTICAS</ListSubheader>
                                    {templates.filter(c => !c.agrupador).map((template, index) => (
                                        <ListItem key={index}>
                                            <Tooltip title={isTemplateVisible(template) ? 'Vísivel no Afunilamento' : 'Oculto no Afunilamento'}>
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        {isTemplateVisible(template) ? <Visibility /> : <VisibilityOff />}
                                                    </Avatar>
                                                </ListItemAvatar>
                                            </Tooltip>
                                            <ListItemText
                                                primary={template.template}
                                                secondary={`${tiposCampos[template.tipo as keyof typeof tiposCampos]}${template.heranca_template_label ? ` | Atrelado: ${template.heranca_template_label} (${template.heranca_label})` : ''}`}
                                            />
                                            <ListItemSecondaryAction>
                                                {Boolean(modelo.composto) &&
                                                    <Tooltip title="Tornar Agrupador">
                                                        <IconButton edge="start" onClick={() => setAgrupador(template)}>
                                                            <More />
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                                <IconButton onClick={() => editTemplate(template)}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton disabled={template.id ? true : false} edge="end" onClick={() => removeTemplate(template)}>
                                                    <Delete />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                        <Dialog open={editDialog} fullWidth={true} maxWidth="xs">
                            <AppBar style={{ position: 'relative' }}>
                                <DialogTitle>Editar Característica</DialogTitle>
                                <Box px={3} mb={2}>
                                    <Typography variant="body2" color="initial">
                                        Preencha as informações da característica.
                                    </Typography>
                                </Box>
                                <Fade in={isLoading('atrelamentos')} timeout={500}>
                                    <LinearProgress color="secondary"></LinearProgress>
                                </Fade>
                                <Tabs centered value={tab} onChange={(_event, value) => setTab(value)}>
                                    <Tab label="Característica" />
                                    <Tab label="Atrelar" />
                                </Tabs>
                            </AppBar>
                            <DialogContent>
                                <Box display={tab === 0 ? 'block' : 'none'} mt={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Descrição"
                                                variant="outlined"
                                                size="small"
                                                fullWidth={true}
                                                value={editValue.template || ''}
                                                onChange={(event) => setEditValue({ ...editValue, template: event.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box display={tab === 1 ? 'block' : 'none'} mt={2}>
                                    {!isLoading('atrelamentos') &&
                                        <List>
                                            {subtiposData && subtiposData.length > 0 ? subtiposData.map((subtipo, index) => (
                                                <ListItemCollapse key={index} title={subtipo.subtipo}>
                                                    {subtipo.templates.map((t, i) => (
                                                        <ListItem button key={i} onClick={() => selectEditAtrelar(subtipo, t)}>
                                                            <ListItemIcon>
                                                                <Checkbox
                                                                    edge="end"
                                                                    checked={t.id === editValue.heranca_id}
                                                                    tabIndex={-1}
                                                                    disableRipple={true}
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t.template}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </ListItemCollapse>
                                            )) :
                                                <ListItem>
                                                    <ListItemText primary="Nenhuma possibilidade de atrelamento encontrado." />
                                                </ListItem>
                                            }
                                        </List>
                                    }
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button color="primary" onClick={() => setEditDialog(false)}>
                                    Fechar
                                </Button>
                                <Button color="primary" onClick={() => saveTemplate()}>
                                    Salvar
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Dialog open={agrupadorDialog} fullWidth={true} maxWidth="xs">
                            <AppBar style={{ position: 'relative' }}>
                                <DialogTitle>Editar Agrupador</DialogTitle>
                                <Box px={3} mb={2}>
                                    <Typography variant="body2" color="initial">
                                        Preencha as informações do agrupador.
                                    </Typography>
                                </Box>
                            </AppBar>
                            <DialogContent>
                                <Box mt={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Descrição"
                                                variant="outlined"
                                                size="small"
                                                fullWidth={true}
                                                value={agrupadorValue.template || ''}
                                                onChange={(event) => setAgrupadorValue({ ...agrupadorValue, template: event.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button color="primary" onClick={() => setAgrupadorDialog(false)}>
                                    Fechar
                                </Button>
                                <Button color="primary" onClick={() => saveAgrupador()}>
                                    Salvar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
    })
);

export default ModelosCreate;